import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { buildProductPath } from '../utils/productSlug';
import { collection, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import usePageView from '../hooks/usePageView';
import ProductGridItem from './ProductGridItem';
import { useDisplayPreferences } from '../contexts/DisplayPreferencesContext';
import { MAX_CONTENT_WIDTH, BORDER_RADIUS, SPACING_BUTTON, SPACING_CARD, SPACING_CARD_BODY, SPACING_GROUP, SPACING_SECTION, SPACING_COMPACT, SPACING_COMPONENT, SPACING_ELEMENT } from '../constants/layout';
import { FONT_LARGE_TITLE, FONT_LARGE_TITLE_MOBILE, FONT_TITLE, FONT_TITLE2, FONT_TITLE2_MOBILE, FONT_TITLE3, FONT_HEADLINE, FONT_SUBHEADLINE, FONT_BODY, FONT_CALLOUT, FONT_FOOTNOTE, FONT_CAPTION, FONT_CAPTION2 } from '../constants/typography';
import { COLORS } from '../constants/colors';

const parseDate = value =>
  !value ? null : value.toDate ? value.toDate() : new Date(value);

// CategorySelect stays the same
// function CategorySelect({ categories, selected, onChange }) {
//   return (
//     <select value={selected} onChange={onChange}>
//       <option value="">All Categories</option>
//       {categories.map(cat => (
//         <option key={cat.id} value={cat.id}>
//           {cat.title}
//         </option>
//       ))}
//     </select>
//   );
// }

function HeroFadeImage({ src, alt }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onLoad={() => setLoaded(true)}
      style={{
        opacity: loaded ? 1 : 0,
        transition: 'opacity 0.4s ease-in',
      }}
    />
  );
}

function BrowseDesignsBanner({ products, getCoverImage }) {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);

  const productsWithImage = products.filter(product => {
    const cover = getCoverImage(product);
    return cover && cover.imageURL && cover.imageURL.trim() !== '';
  });

  const advanceImage = useCallback(() => {
    if (productsWithImage.length === 0) return;
    setCurrentIndex(prev => (prev + 1) % productsWithImage.length);
  }, [productsWithImage.length]);

  useEffect(() => {
    if (productsWithImage.length <= 1) return;
    const interval = setInterval(advanceImage, 4000);
    return () => clearInterval(interval);
  }, [advanceImage, productsWithImage.length]);

  if (productsWithImage.length === 0) return null;

  return (
    <div className="browse-banner">
      <div className="browse-banner-images">
        {productsWithImage.map((product, i) => {
          const cover = getCoverImage(product);
          return (
            <img
              key={product.id}
              src={cover.thumbnailURL || cover.imageURL}
              alt={product.productName}
              className={`browse-banner-img ${i === currentIndex ? 'active' : ''}`}
            />
          );
        })}
        <div className="browse-banner-overlay" />
      </div>
      <div className="browse-banner-content">
        <h2 className="browse-banner-title">Explore Our Gallery</h2>
        <p className="browse-banner-subtitle">
          Discover arrangements crafted with care for every occasion
        </p>
        <button
          className="browse-banner-button"
          onClick={() => navigate('/browsedesigns')}
        >
          Browse Designs
        </button>
      </div>
      <style jsx>{`
        .browse-banner {
          position: relative;
          width: 100%;
          max-width: ${MAX_CONTENT_WIDTH};
          margin: 0 auto ${SPACING_SECTION};
          overflow: hidden;
          border: 1px solid var(--color-border, #e5e7eb);
          height: 320px;
        }
        .browse-banner-images {
          position: absolute;
          inset: 0;
        }
        .browse-banner-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 1.5s ease-in-out;
        }
        .browse-banner-img.active {
          opacity: 1;
        }
        .browse-banner-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to right,
            rgba(0, 0, 0, 0.65) 0%,
            rgba(0, 0, 0, 0.4) 50%,
            rgba(0, 0, 0, 0.15) 100%
          );
        }
        .browse-banner-content {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          height: 100%;
          padding: ${SPACING_SECTION};
          color: #ffffff;
        }
        .browse-banner-title {
          margin: 0 0 ${SPACING_COMPACT};
          font-size: ${FONT_TITLE2};
          font-family: 'Instrument Serif', serif;
          font-weight: 700;
          line-height: 1.2;
          letter-spacing: -0.01em;
        }
        .browse-banner-subtitle {
          margin: 0 0 ${SPACING_GROUP};
          font-size: ${FONT_BODY};
          opacity: 0.85;
          max-width: 400px;
          line-height: 1.5;
        }
        .browse-banner-button {
          padding: ${SPACING_BUTTON} 28px;
          background-color: #ffffff;
          color: #000000;
          border: none;
          border-radius: ${BORDER_RADIUS};
          font-size: ${FONT_BODY};
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s ease, transform 0.2s ease;
        }
        .browse-banner-button:hover {
          background-color: #f3f4f6;
          transform: translateY(-1px);
        }
        @media (max-width: 768px) {
          .browse-banner {
            height: 260px;
          }
          .browse-banner-content {
            padding: ${SPACING_SECTION} ${SPACING_GROUP};
          }
          .browse-banner-title {
            font-size: ${FONT_TITLE};
          }
          .browse-banner-subtitle {
            font-size: ${FONT_CALLOUT};
          }
        }
      `}</style>
    </div>
  );
}

function CategoryBanner({ category, productCount, imageURLs }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (imageURLs.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % imageURLs.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [imageURLs.length]);

  return (
    <Link to={`/category/${category.id}`} className="cat-banner">
      {imageURLs.map((url, i) => (
        <img
          key={url}
          src={url}
          alt={category.title}
          className={`cat-banner-bg ${i === currentIndex ? 'active' : ''}`}
        />
      ))}
      <div className="cat-banner-overlay" />
      <div className="cat-banner-content">
        <h2 className="cat-banner-title">{category.title}</h2>
        {category.subtitle && (
          <p className="cat-banner-subtitle">{category.subtitle}</p>
        )}
        <span className="cat-banner-count">
          {productCount} {productCount === 1 ? 'design' : 'designs'}
        </span>
      </div>
    </Link>
  );
}

// New Hero Section Component with Continuous Scrolling
function HeroSection({ products, imageItems, getCoverImage, categories }) {
  const navigate = useNavigate();
  const { showPrices } = useDisplayPreferences();
  // 1) Only keep products that actually have a cover image
  const productsWithImage = products.filter(product => {
    const coverImage = getCoverImage(product);
    return coverImage && coverImage.imageURL && coverImage.imageURL.trim() !== '';
  });

  // 2) Duplicate that filtered list for seamless infinite scroll
  const featuredProducts = [...productsWithImage, ...productsWithImage];

  const carouselRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const scrollTimeout = useRef(null);
  const scrollPos = useRef(0);

  const handleUserScroll = () => {
    setAutoScroll(false);
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    scrollPos.current = carouselRef.current?.scrollLeft || 0;
    scrollTimeout.current = setTimeout(() => setAutoScroll(true), 2000);
  };

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    scrollPos.current = el.scrollLeft;
    let rafId;
    const tick = () => {
      if (autoScroll) {
        scrollPos.current += 0.5;
        const maxScroll = el.scrollWidth / 2;
        if (scrollPos.current >= maxScroll) {
          scrollPos.current -= maxScroll;
        }
        el.scrollLeft = scrollPos.current;
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [autoScroll, featuredProducts.length]);

  // Previously used window.location.href which caused a full browser reload and blank screen flash.
  // Fixed by using React Router's navigate() with buildProductPath() for slug+encoded ID URLs.
  const handleProductClick = product => {
    navigate(buildProductPath(product));
  };

  // Helper: find the first category title for a given product ID
  const findCategoryTitle = productId => {
    const cat = categories.find(
      c => Array.isArray(c.itemIDs) && c.itemIDs.includes(productId)
    );
    return cat ? cat.title : '';
  };

  return (
    <div className="hero-section">
      <div className="hero-content">
       <h1 className="hero-title">
  {"Blooms for Every Moment"}
</h1>
        <p className="hero-subtitle">Family-run flower shop in Westminster, California</p>

        <div
          className="hero-carousel-container"
          ref={carouselRef}
          onWheel={handleUserScroll}
          onTouchStart={handleUserScroll}
          onTouchMove={handleUserScroll}
        >
          <div className="hero-carousel">
            {featuredProducts.map((product, index) => {
              const coverImage = getCoverImage(product);
              const categoryTitle = findCategoryTitle(product.id);
              return (
                <div
                  key={`${product.id}-${index}`}
                  className="hero-product-card"
                  onClick={() => handleProductClick(product)}
                >
                  <div className="hero-product-image">
                    {coverImage ? (
                      <HeroFadeImage
                        src={coverImage.thumbnailURL || coverImage.imageURL}
                        alt={product.productName}
                      />
                    ) : (
                      <div className="hero-product-placeholder">
                        <span className="material-icons">image</span>
                      </div>
                    )}
                  </div>
                  <div className="hero-product-info">
                    {categoryTitle && (
                      <div className="hero-product-category">
                        {categoryTitle}
                      </div>
                    )}
                    <h3 className="hero-product-name">{product.productName}</h3>
                    {showPrices && product.price && (
                      <p className="hero-product-price">${product.price}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <style jsx>{`
        .hero-section {
          background: ${COLORS.lightBackground};
          color: #000;
          padding: ${SPACING_SECTION} 0;
          margin-bottom: ${SPACING_SECTION};
          position: relative;
          overflow: hidden;
          width: 100vw;
          margin-left: calc(-50vw + 50%);
        }

        .hero-content {
          position: relative;
          z-index: 2;
          text-align: center;
          color: #000;
        }

        .hero-title {
          font-size: ${FONT_LARGE_TITLE};
          font-weight: 700;
          margin: 0 0 ${SPACING_BUTTON} 0;
          font-family: 'Instrument Serif', serif;
          color: #000;
          text-shadow: none;
        }

        .hero-subtitle {
          font-size: ${FONT_SUBHEADLINE};
          margin: 0 0 ${SPACING_GROUP} 0;
          font-weight: 300;
          color: #000;
        }

        .hero-carousel-container {
          width: 100%;
          overflow-x: auto;
          overflow-y: hidden;
          position: relative;
          scrollbar-width: none;
          touch-action: pan-x;
          min-height: 280px;
        }

        .hero-carousel-container::-webkit-scrollbar {
          display: none;
        }

        /* Increased gap: 40px */
        .hero-carousel {
          display: flex;
          gap: ${SPACING_CARD};
        }

        /* 1:1 aspect ratio, no rounded corners, thin border */
       .hero-product-card {
  min-width: 280px;
  width: 280px;
  height: 280px;
  border-radius: 0;
  border: 0.5px solid #888888;
  overflow: hidden;
  transition: transform 0.3s ease;
  position: relative;
  display: flex;
  flex-direction: column;
  cursor: pointer;
}


        .hero-product-card:hover {
          transform: scale(1.02);
        }

        /* Image covers full square */
        .hero-product-image {
          width: 100%;
          height: 100%;
          position: relative;
          overflow: hidden;
          flex: 1;
        }

        .hero-product-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .hero-product-card:hover .hero-product-image img {
          transform: scale(1.05);
        }

        .hero-product-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.05);
          color: rgba(255, 255, 255, 0.4);
        }

        .hero-product-placeholder .material-icons {
          font-size: 40px;
        }

        /* Info area aligned left */
        .hero-product-info {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: ${SPACING_COMPONENT} ${SPACING_BUTTON};
          background: linear-gradient(
            to top,
            rgba(0, 0, 0, 0.6) 0%,
            rgba(0, 0, 0, 0.4) 40%,
            rgba(0, 0, 0, 0.2) 70%,
            rgba(0, 0, 0, 0.05) 85%,
            rgba(0, 0, 0, 0) 100%
          );
          color: white;
          border-radius: 0;
          padding-top: ${SPACING_SECTION};
          text-align: left;
        }

        /* Smaller category label */
        .hero-product-category {
          font-size: ${FONT_CAPTION};
          margin: 0 0 4px 0;
          font-weight: 400;
          opacity: 0.85;
          font-family: 'Instrument Serif', serif;
        }

.hero-product-name {
  font-size: ${FONT_HEADLINE};
  font-weight: 600;
  margin: 0 0 4px 0;
  font-family: 'Instrument Serif', serif;
  line-height: 1.3;
  /* larger blur (spread) */
  text-shadow: 1px 1px 6px rgba(0, 0, 0, 0.8);
}

        .hero-product-price {
          font-size: ${FONT_BODY};
          font-weight: 700;
          color: #4ade80;
          margin: 0;
          text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
        }

        @media (max-width: 768px) {
          .hero-title {
            font-size: ${FONT_LARGE_TITLE_MOBILE};
          }

          .hero-subtitle {
            font-size: ${FONT_BODY};
            margin-bottom: ${SPACING_BUTTON};
          }

          .hero-carousel-container {
            min-height: 240px;
          }

          .hero-product-card {
            min-width: 240px;
            width: 240px;
            height: 240px;
          }

          .hero-product-info {
            padding: ${SPACING_COMPACT} ${SPACING_COMPONENT};
          }

          .hero-product-category {
            font-size: ${FONT_CAPTION2};
            margin-bottom: 2px;
          }

          .hero-product-name {
            font-size: ${FONT_BODY};
          }

          .hero-product-price {
            font-size: ${FONT_CALLOUT};
          }

          .hero-carousel {
            gap: ${SPACING_BUTTON};
          }
        }
      `}</style>
    </div>
  );
}

export default function CatalogView() {
  usePageView('home');
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm] = useState('');
  const [selectedCategory] = useState('');
  const [imageItems, setImageItems] = useState([]);
  const [loadedThumbnails, setLoadedThumbnails] = useState(new Set());

  // 1) Set document title on mount
  useEffect(() => {
    document.title = 'Catalog';
  }, []);

  // 2) Fetch all products once
  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, 'products'));
      setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    })();
  }, []);

  // 3) Fetch all categories once
  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, 'categories'));
      setCategories(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    })();
  }, []);

  // 4) Real-time listener for all imageItems
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'imageItems'), snap => {
      const allImages = snap.docs.map(d => {
        const x = d.data();
        return {
          id: d.id,
          caption: x.caption ?? '',
          imageURL: x.imageURL ?? '',
          thumbnailURL: x.thumbnailURL ?? '',
          productID: x.productID ?? null,
          dateAdded: parseDate(x.dateAdded),
        };
      });
      setImageItems(allImages);
    });
    return () => unsub();
  }, []);

  // 5) Helper: find cover image for a given product
  const getCoverImage = product => {
    let coverImage = null;
    if (product.productImage?.imageItemID) {
      coverImage = imageItems.find(
        img => img.id === product.productImage.imageItemID
      );
    }
    if (!coverImage) {
      const productImages = imageItems.filter(img => img.productID === product.id);
      if (productImages.length > 0) {
        coverImage = productImages[0];
      }
    }
    return coverImage || null;
  };

  // 6) When a thumbnail finishes loading, mark it as loaded
  const handleThumbnailLoad = productId => {
    setLoadedThumbnails(prev => {
      const next = new Set(prev);
      next.add(productId);
      return next;
    });
  };

  const search = searchTerm.trim().toLowerCase();

  const isProductActive = product => {
    const deactivatedDate = parseDate(product.deactivatedDate);
    if (!deactivatedDate) return true;
    const today = new Date();
    return today <= deactivatedDate;
  };

  const activeProducts = products.filter(isProductActive);

  // 7) Filter + sort logic
  let filteredProducts = [];
  if (selectedCategory) {
    const cat = categories.find(c => c.id === selectedCategory);
    if (cat) {
      filteredProducts = activeProducts
        .filter(
          prod =>
            prod.productName.toLowerCase().includes(search) &&
            Array.isArray(cat.itemIDs) &&
            cat.itemIDs.includes(prod.id)
        )
        .sort((a, b) => a.productName.localeCompare(b.productName));
    }
  } else {
    filteredProducts = activeProducts
      .filter(prod => prod.productName.toLowerCase().includes(search))
      .sort((a, b) => a.productName.localeCompare(b.productName));
  }

  return (
    <div>
      {/* ★ Hero Section now uses 1:1 images, left-aligned text, and a thin border */}
      <HeroSection
        products={activeProducts}
        imageItems={imageItems}
        getCoverImage={getCoverImage}
        categories={categories}
      />

      <BrowseDesignsBanner
        products={activeProducts}
        getCoverImage={getCoverImage}
      />

      <div className="contact-banner">
        <div className="contact-banner-inner">
          <div className="contact-banner-text">
            <h2 className="contact-banner-title">Have a special request?</h2>
            <p className="contact-banner-subtitle">
              We'd love to help you find the perfect arrangement. Reach out anytime.
            </p>
          </div>
          <button
            className="contact-banner-button"
            onClick={() => navigate('/store')}
          >
            Get in Touch
          </button>
        </div>
        <style jsx>{`
          .contact-banner {
            max-width: ${MAX_CONTENT_WIDTH};
            margin: 0 auto ${SPACING_SECTION};
            padding: 0 ${SPACING_CARD};
            padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
            padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
          }
          .contact-banner-inner {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: ${SPACING_SECTION};
            padding: ${SPACING_SECTION} ${SPACING_SECTION};
            background-color: #000000;
            color: #ffffff;
          }
          .contact-banner-text {
            flex: 1;
          }
          .contact-banner-title {
            margin: 0 0 ${SPACING_ELEMENT};
            font-size: ${FONT_TITLE};
            font-family: 'Instrument Serif', serif;
            font-weight: 700;
            letter-spacing: -0.01em;
          }
          .contact-banner-subtitle {
            margin: 0;
            font-size: ${FONT_HEADLINE};
            opacity: 0.75;
            line-height: 1.5;
          }
          .contact-banner-button {
            flex-shrink: 0;
            padding: ${SPACING_BUTTON} 28px;
            background-color: #ffffff;
            color: #000000;
            border: none;
            border-radius: ${BORDER_RADIUS};
            font-size: ${FONT_BODY};
            font-weight: 600;
            cursor: pointer;
            transition: background-color 0.2s ease, transform 0.2s ease;
          }
          .contact-banner-button:hover {
            background-color: #f3f4f6;
            transform: translateY(-1px);
          }
          @media (max-width: 600px) {
            .contact-banner-inner {
              flex-direction: column;
              align-items: flex-start;
              padding: ${SPACING_SECTION} ${SPACING_GROUP};
              gap: ${SPACING_CARD_BODY};
            }
          }
        `}</style>
      </div>

      <div className="category-banners-container">
        {categories.map(cat => {
          const productsInCat = activeProducts.filter(prod =>
            Array.isArray(cat.itemIDs) && cat.itemIDs.includes(prod.id)
          );
          if (productsInCat.length === 0) return null;

          const imageURLs = productsInCat
            .map(p => { const c = getCoverImage(p); return c?.thumbnailURL || c?.imageURL; })
            .filter(Boolean);

          return (
            <CategoryBanner
              key={cat.id}
              category={cat}
              productCount={productsInCat.length}
              imageURLs={imageURLs}
            />
          );
        })}

        <style jsx>{`
          .category-banners-container {
            max-width: ${MAX_CONTENT_WIDTH};
            margin: 0 auto;
            padding: 0 ${SPACING_CARD} ${SPACING_SECTION};
            padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
            padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: ${SPACING_CARD};
          }

          .cat-banner {
            position: relative;
            display: flex;
            align-items: flex-end;
            overflow: hidden;
            height: 280px;
            background-color: #1a1a1a;
            text-decoration: none;
            color: #ffffff;
            transition: transform 0.3s ease;
          }

          .cat-banner:hover {
            transform: translateY(-2px);
          }

          .cat-banner-bg {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            opacity: 0;
            transition: opacity 1.5s ease-in-out;
          }

          .cat-banner-bg.active {
            opacity: 1;
          }

          .cat-banner-overlay {
            position: absolute;
            inset: 0;
            background: linear-gradient(
              to top,
              rgba(0, 0, 0, 0.7) 0%,
              rgba(0, 0, 0, 0.3) 50%,
              rgba(0, 0, 0, 0.05) 100%
            );
          }

          .cat-banner-content {
            position: relative;
            z-index: 1;
            padding: ${SPACING_GROUP} ${SPACING_SECTION};
            width: 100%;
          }

          .cat-banner-title {
            margin: 0 0 4px;
            font-size: ${FONT_TITLE};
            font-family: 'Instrument Serif', serif;
            font-weight: 700;
            line-height: 1.2;
            letter-spacing: -0.01em;
          }

          .cat-banner-subtitle {
            margin: 0 0 ${SPACING_COMPACT};
            font-size: ${FONT_CALLOUT};
            opacity: 0.8;
            line-height: 1.4;
          }

          .cat-banner-count {
            font-size: ${FONT_FOOTNOTE};
            opacity: 0.6;
            letter-spacing: -0.01em;
            text-transform: uppercase;
          }

          @media (max-width: 768px) {
            .category-banners-container {
              grid-template-columns: 1fr;
            }
            .cat-banner {
              height: 220px;
            }
            .cat-banner-content {
              padding: ${SPACING_GROUP};
            }
            .cat-banner-title {
              font-size: ${FONT_TITLE};
            }
          }
        `}</style>
      </div>
    </div>
  );
}
